/** Step index, section progress, back stack, `#s=<id>` hash sync. */
import type { Funnel, Step } from './types.ts';

export interface SegmentProgress { section: string; width: number }

export const indexOf = (funnel: Funnel, id: string): number => funnel.steps.findIndex((s) => s.id === id);

export const stepAt = (funnel: Funnel, i: number): Step =>
  funnel.steps[Math.max(0, Math.min(funnel.steps.length - 1, i))];

/** the id of the step after `i`, honouring an explicit `next`. */
export function nextId(funnel: Funnel, i: number): string | null {
  const step = funnel.steps[i];
  if (!step) return null;
  if (step.next) return step.next;
  return funnel.steps[i + 1]?.id ?? null;
}

/**
 * 6 segments (one per section): earlier sections full, the current one fills
 * (posInSection + 1) / stepsInSection, later ones empty.
 */
export function progress(funnel: Funnel, i: number): SegmentProgress[] {
  const step = stepAt(funnel, i);
  const section = step.section ?? 0;
  const inSection = funnel.steps.filter((s) => s.section === section);
  const pos = Math.max(0, inSection.findIndex((s) => s.id === step.id));
  return funnel.sections.map((name, si) => ({
    section: name,
    width: si < section ? 100 : si === section ? Math.round(((pos + 1) / inSection.length) * 100) : 0,
  }));
}

export const sectionLabel = (funnel: Funnel, i: number): string =>
  funnel.sections[stepAt(funnel, i).section ?? 0] ?? '';

export function readHash(): string | null {
  const m = /(?:^|[#&])s=([^&]+)/.exec(location.hash);
  return m ? decodeURIComponent(m[1]) : null;
}

export function writeHash(id: string): void {
  const next = `#s=${encodeURIComponent(id)}`;
  if (location.hash !== next) history.replaceState(null, '', next);
}

/** A back stack that survives auto-advance: loaders and their targets are never re-entered backwards. */
export class BackStack {
  private stack: number[] = [];

  push(i: number): void {
    if (this.stack[this.stack.length - 1] !== i) this.stack.push(i);
  }

  pop(): number | undefined {
    this.stack.pop();
    return this.stack[this.stack.length - 1];
  }

  get depth(): number {
    return this.stack.length;
  }

  reset(i: number): void {
    this.stack = [i];
  }
}
